/**
 * Excel导出工具函数
 * 提供Excel导出相关的实用函数
 */

/**
 * 处理导出文件下载
 * @param {string} url - 文件下载链接
 * @param {string} filename - 文件名
 */
import { getPresignedViewUrl } from "../api/attachmentApi";
import { authFetch } from "../api/authFetch";

export const handleExportDownload = async (url, filename) => {
    // 确保链接是有效的
    if (!url) {
        console.error("下载链接无效");
        return;
    }

    try {
        let finalUrl = url;

        // 导出接口若已返回签名链接，直接用，避免再走一次换签。
        // 计划名里的不可见字符会导致对象键和浏览器解析后的路径不一致，二次换签 404。
        const alreadySigned = (() => {
            try {
                const parsed = new URL(url);
                return parsed.searchParams.has("OSSAccessKeyId") || parsed.searchParams.has("Signature");
            } catch {
                return false;
            }
        })();

        if (!alreadySigned) {
            try {
                let objectKey = null;
                if (typeof url === "string" && /^https?:\/\//i.test(url)) {
                    const u = new URL(url);
                    objectKey = decodeURIComponent(u.pathname.replace(/^\//, ""));
                } else if (typeof url === "string") {
                    objectKey = url.replace(/^\//, "");
                }

                if (objectKey) {
                    const data = await getPresignedViewUrl(objectKey);
                    if (data?.signed_url) {
                        finalUrl = data.signed_url;
                    }
                }
            } catch (e) {
                console.warn("获取预签名查看链接失败，回退到原链接:", e);
            }
        }

        const link = document.createElement("a");
        link.href = finalUrl;
        link.setAttribute("download", filename || "export.xlsx");
        link.setAttribute("target", "_blank"); // 添加target属性，避免某些浏览器的安全限制
        document.body.appendChild(link);

        // 使用click()方法模拟点击
        link.click();

        // 延迟一小段时间后移除链接元素
        setTimeout(() => {
            document.body.removeChild(link);
        }, 1000); // 给予下载开始的时间
    } catch (error) {
        console.error("下载文件时出错:", error);
        // 作为备选方案，尝试直接打开链接
        window.open(url, "_blank");
    }
};

/**
 * 轮询导出任务状态
 * @param {string} taskId - 任务ID
 * @param {Function} onProgress - 进度回调函数 (status) => void
 * @param {Function} onComplete - 完成回调函数 (result) => void
 * @param {Function} onError - 错误回调函数 (error) => void
 * @param {number} interval - 轮询间隔(毫秒)
 * @param {number} maxAttempts - 最大轮询次数
 */
export const pollExportTaskStatus = (taskId, onProgress, onComplete, onError, interval = 1000, maxAttempts = 60) => {
    let attempts = 0;

    const checkStatus = async () => {
        try {
            if (attempts >= maxAttempts) {
                onError(new Error("导出任务超时"));
                return;
            }

            attempts++;

            // 这里需要调用API获取任务状态
            const response = await authFetch(`/v1/export/tasks/${taskId}`, {}, { entrypoint: "export-task-poll" });
            const status = await response.json();

            if (status.status === "COMPLETED") {
                onComplete(status.result);
                return;
            } else if (status.status === "FAILED") {
                onError(new Error(status.error || "导出任务失败"));
                return;
            } else {
                // 任务仍在进行中
                if (onProgress) {
                    onProgress(status);
                }

                // 继续轮询
                setTimeout(checkStatus, interval);
            }
        } catch (error) {
            onError(error);
        }
    };

    // 开始轮询
    checkStatus();
};

/**
 * 生成默认的导出文件名
 * @param {string} prefix - 文件名前缀
 * @param {string} extension - 文件扩展名（默认为xlsx）
 * @returns {string} 格式化的文件名
 */
export const generateExportFilename = (prefix, extension = "xlsx") => {
    const date = new Date();
    const timestamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}_${String(date.getHours()).padStart(2, "0")}${String(date.getMinutes()).padStart(2, "0")}`;
    return `${prefix}_${timestamp}.${extension}`;
};
